/**
 * useDiagHistory(diagState, chatId, getStorage?): the diag_state of every turn of this chat, oldest first, for the Graph view's
 * turn breakdown. Returns [{seq, turnNo, answers, diag}] (turnHistoryStore).
 *
 * Hydrates from localStorage (per chat, keyed by the session id) before any live state arrives, then merges each new
 * tse:diag_state by seq (a repeat replaces, a new diagnosis_id starts over) and writes the chat back. Works without storage.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import { defaultStorage, loadChat, mergeTurn, saveChat } from "./turnHistoryStore";

const EMPTY = Object.freeze([]);

export default function useDiagHistory(diagState, chatId, getStorage = defaultStorage) {
  const key = chatId || diagState?.session_id || null;
  const [storage] = useState(() => getStorage());
  const hydrated = useMemo(() => (key ? loadChat(storage, key) : EMPTY), [storage, key]);
  const [live, setLive] = useState({ key, turns: hydrated });
  // Content signature: a parent handing a fresh-but-equal diagState each render must not re-merge (render loop).
  const diagSignature = useMemo(() => (diagState?.category ? JSON.stringify(diagState) : ""), [diagState]);
  const diagRef = useRef(diagState);
  diagRef.current = diagState;

  useEffect(() => {
    if (!diagSignature) return;
    setLive((prev) => {
      const base = prev.key === key ? prev.turns : loadChat(storage, key);
      return { key, turns: mergeTurn(base, diagRef.current) };
    });
  }, [diagSignature, key, storage]);

  useEffect(() => {
    if (live.key && live.key === key && live.turns.length) saveChat(storage, key, live.turns);
  }, [live, key, storage]);

  return live.key === key ? live.turns : hydrated;
}
